import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, merchantWebhooks } from '@/api/client';
import { Empty } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/m/webhooks')({
  component: MerchantWebhooks,
});

function MerchantWebhooks() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['merchant-webhooks'],
    queryFn: merchantWebhooks.list,
  });
  const create = useMutation({
    mutationFn: (body: Parameters<typeof merchantWebhooks.create>[0]) =>
      merchantWebhooks.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant-webhooks'] }),
  });
  const deactivate = useMutation({
    mutationFn: (id: string) => merchantWebhooks.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant-webhooks'] }),
  });

  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');

  const onCreate = () => {
    if (!url || secret.length < 16) {
      toast.error(t('common.error') as string);
      return;
    }
    create.mutate(
      { url, secret, events: ['*'] },
      {
        onSuccess: () => {
          setOpen(false);
          setUrl('');
          setSecret('');
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'failed'),
      },
    );
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('nav.webhooks')}</h1>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> {t('common.create')}
        </Button>
      </header>

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>URL</TableHead>
              <TableHead>{t('payments.cols.status')}</TableHead>
              <TableHead>{t('payments.cols.created')}</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-xs">{e.url}</TableCell>
                <TableCell>
                  <Badge variant={e.active ? 'success' : 'outline'}>
                    {e.active ? t('common.active') : t('common.disabled')}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {dayjs(e.created_at).format('YYYY-MM-DD')}
                </TableCell>
                <TableCell className="text-right">
                  {e.active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deactivate.mutate(e.id)}
                      disabled={deactivate.isPending}
                    >
                      {t('common.disabled')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!list.isLoading && list.data?.length === 0 && (
          <Empty title={t('common.none')} className="border-0" />
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.create')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="url">URL</Label>
              <Input id="url" value={url} onChange={(e) => setUrl(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="secret">Secret (≥ 16 chars)</Label>
              <Input
                id="secret"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={onCreate} disabled={create.isPending}>
              {t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
